import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import {
  DrawDetail,
  DrawTimelineEvent,
  DrawInspectionData,
  GcProgressDisplayItem,
  fmtMoney,
} from "Pages/Admin/ProjectDetail/sections/DrawHistory/DrawHistory.types";
import { formatDate } from "utils/Common/Date";

export interface DrawHistoryEntry {
  drawDetail: DrawDetail;
  timelineEvents?: DrawTimelineEvent[];
  inspectionData?: DrawInspectionData | null;
  progressLineItems?: GcProgressDisplayItem[];
}

export interface DrawHistoryPdfProps {
  entries: DrawHistoryEntry[];
  projectName?: string;
}

// Lifted from the app's :root CSS variables (App.scss) so the PDF reads as
// an extension of the on-screen Draw History UI rather than a generic report.
const COLORS = {
  b: "#14508c",
  bl: "#e6f0f8",
  bm: "#4a80b8",
  bdk: "#0d3a66",
  bg3: "#edf1f5",
  bd: "#dde1e6",
  tx: "#111111",
  tx2: "#555c66",
  tx3: "#909399",
  green: "#1a6b35",
  gbg: "#eaf6ee",
  gbd: "#9fd3b0",
  amber: "#7a5c00",
  abg: "#fff8e1",
  abd: "#e8c820",
  red: "#a32d2d",
  rbg: "#fcebeb",
  rbd: "#f09595",
};

/* ── Styles ──────────────────────────────────────────────────────── */

const S = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 36,
    paddingHorizontal: 30,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLORS.tx,
    backgroundColor: "#ffffff",
  },

  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.bd,
  },
  headerTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 14,
    color: COLORS.b,
    marginBottom: 3,
  },
  headerSub: { fontSize: 7.5, color: COLORS.tx3, marginTop: 2 },
  headerMetaLine: { fontSize: 8, color: COLORS.tx2, marginBottom: 2 },
  headerRight: { alignItems: "flex-end" },

  /* Draw header banner — mirrors on-screen .drawHeader */
  dhCard: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    backgroundColor: COLORS.bdk,
    borderRadius: 10,
    paddingVertical: 16,
    paddingHorizontal: 18,
    marginBottom: 14,
  },
  dhBadge: {
    alignSelf: "flex-start",
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 3,
    paddingHorizontal: 9,
    marginBottom: 8,
  },
  dhBadgeApproved: {
    backgroundColor: "rgba(34,197,94,0.2)",
    borderColor: "rgba(34,197,94,0.5)",
    color: "#86efac",
  },
  dhBadgePending: {
    backgroundColor: "rgba(245,158,11,0.2)",
    borderColor: "rgba(245,158,11,0.5)",
    color: "#fcd34d",
  },
  dhBadgeRejected: {
    backgroundColor: "rgba(239,68,68,0.2)",
    borderColor: "rgba(239,68,68,0.5)",
    color: "#fca5a5",
  },
  dhTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 15,
    color: "#ffffff",
    marginBottom: 5,
  },
  dhSub: { fontSize: 8.5, color: "rgba(255,255,255,0.78)", lineHeight: 1.5 },
  dhRight: { alignItems: "flex-end" },
  dhAmountLabel: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: "rgba(255,255,255,0.6)",
    letterSpacing: 0.6,
    marginBottom: 3,
  },
  dhAmount: { fontSize: 18, fontFamily: "Helvetica-Bold", color: "#ffffff" },
  dhAmountSub: {
    fontSize: 7.5,
    color: "rgba(255,255,255,0.6)",
    marginTop: 3,
  },

  /* Card — mirrors on-screen .card / .cardTitle */
  card: {
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginBottom: 14,
    backgroundColor: "#ffffff",
  },
  cardTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 11,
    paddingBottom: 9,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.bd,
  },
  cardTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: COLORS.tx,
    letterSpacing: 0.5,
  },

  /* G702 lines — mirrors .g702Table */
  g702Line: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 4,
    borderBottomWidth: 0.75,
    borderBottomColor: COLORS.bd,
  },
  g702LineTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 7,
    paddingHorizontal: 4,
    borderTopWidth: 1.5,
    borderTopColor: COLORS.b,
    marginTop: 3,
  },
  g702Left: { flexDirection: "row", alignItems: "center", flex: 1 },
  g702Num: {
    width: 26,
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.tx3,
  },
  g702Label: { fontSize: 8.5, color: COLORS.tx2, flex: 1 },
  g702LabelBold: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8.5,
    color: COLORS.tx,
  },
  g702Val: {
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
    width: 110,
  },
  g702ValTotal: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
    width: 110,
  },

  /* Generic table — mirrors .aiaTable */
  tHead: {
    flexDirection: "row",
    backgroundColor: COLORS.b,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderWidth: 1,
    borderColor: COLORS.bdk,
  },
  tRow: {
    flexDirection: "row",
    borderLeftWidth: 0.5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: COLORS.bd,
    paddingVertical: 5,
    paddingHorizontal: 6,
  },
  tRowAlt: {
    flexDirection: "row",
    borderLeftWidth: 0.5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: COLORS.bd,
    paddingVertical: 5,
    paddingHorizontal: 6,
    backgroundColor: COLORS.bg3,
  },
  tTotal: {
    flexDirection: "row",
    backgroundColor: COLORS.bg3,
    paddingVertical: 7,
    paddingHorizontal: 6,
    borderWidth: 1,
    borderColor: COLORS.bd,
    marginTop: -0.5,
  },
  th: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7.5,
    color: "#ffffff",
    letterSpacing: 0.4,
  },
  td: { fontSize: 8, color: COLORS.tx },
  tdMuted: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.tx3,
    textAlign: "center",
  },
  tdBold: { fontFamily: "Helvetica-Bold", fontSize: 8.5, color: COLORS.tx },
  tdGreen: { fontSize: 8, color: COLORS.green },
  tdWarn: { fontSize: 8, color: COLORS.amber },

  cItem: { width: 48, textAlign: "center" },
  cDesc: { flex: 1, paddingRight: 6 },
  cMoney: { width: 68, textAlign: "right" },
  cPct: { width: 36, textAlign: "right" },

  /* List rows (invoices / waivers) — mirrors .docItem */
  listRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.bd,
    paddingVertical: 7,
    paddingHorizontal: 2,
  },
  docIcon: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 0.75,
    alignItems: "center",
    justifyContent: "center",
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
  },
  docIconPdf: {
    backgroundColor: COLORS.rbg,
    borderColor: COLORS.rbd,
    color: COLORS.red,
  },
  docIconSigned: {
    backgroundColor: COLORS.gbg,
    borderColor: COLORS.gbd,
    color: COLORS.green,
  },
  docIconPending: {
    backgroundColor: COLORS.abg,
    borderColor: COLORS.abd,
    color: COLORS.amber,
  },
  docIconMissing: {
    backgroundColor: COLORS.rbg,
    borderColor: COLORS.rbd,
    color: COLORS.red,
  },
  listBody: { flex: 1 },
  listName: { fontSize: 8.5, color: COLORS.tx, fontFamily: "Helvetica-Bold" },
  listMeta: { fontSize: 7.5, color: COLORS.tx3, marginTop: 1 },
  statusChip: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    borderRadius: 8,
    borderWidth: 0.75,
    paddingVertical: 2,
    paddingHorizontal: 7,
  },
  statusChipSigned: {
    backgroundColor: COLORS.gbg,
    borderColor: COLORS.gbd,
    color: COLORS.green,
  },
  statusChipPending: {
    backgroundColor: COLORS.abg,
    borderColor: COLORS.abd,
    color: COLORS.amber,
  },
  statusChipMissing: {
    backgroundColor: COLORS.rbg,
    borderColor: COLORS.rbd,
    color: COLORS.red,
  },
  emptyNote: { fontSize: 8, color: COLORS.tx3, fontStyle: "italic" },

  /* GC Progress — mirrors .progChip */
  progChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: COLORS.bg3,
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 6,
  },
  progChipNum: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.tx3,
    width: 24,
  },
  progChipDesc: {
    flex: 1,
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.tx,
  },
  progChipStatus: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    borderRadius: 8,
    borderWidth: 0.75,
    paddingVertical: 2,
    paddingHorizontal: 7,
  },
  progChipNotStarted: {
    backgroundColor: COLORS.rbg,
    borderColor: COLORS.rbd,
    color: COLORS.red,
  },
  progChipInProgress: {
    backgroundColor: COLORS.abg,
    borderColor: COLORS.abd,
    color: COLORS.amber,
  },
  progChipNearingComplete: {
    backgroundColor: COLORS.bl,
    borderColor: COLORS.bm,
    color: COLORS.b,
  },

  /* Signatures — mirrors .sigCard / .sigHeader */
  sigRow: { flexDirection: "row", gap: 14 },
  sigCard: {
    flex: 1,
    backgroundColor: COLORS.bg3,
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    borderRadius: 10,
  },
  sigCardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: COLORS.b,
    color: "#ffffff",
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    letterSpacing: 0.4,
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sigBody: { padding: 12 },
  sigLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  sigLabel: { fontSize: 7.5, color: COLORS.tx3 },
  sigValue: { fontSize: 8, fontFamily: "Helvetica-Bold", color: COLORS.tx },

  /* Approval — mirrors .approvalBlock / .approvalIcon */
  approvalBlock: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderRadius: 10,
    borderWidth: 0.75,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginBottom: 14,
    gap: 12,
  },
  approvalBlockApproved: {
    backgroundColor: COLORS.gbg,
    borderColor: COLORS.gbd,
  },
  approvalBlockPending: {
    backgroundColor: COLORS.abg,
    borderColor: COLORS.abd,
  },
  approvalIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
  },
  approvalIconApproved: { backgroundColor: COLORS.green },
  approvalIconPending: { backgroundColor: COLORS.amber },
  approvalTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 11,
    marginBottom: 5,
  },
  approvalTitleApproved: { color: COLORS.green },
  approvalTitlePending: { color: COLORS.amber },
  approvalLine: {
    fontSize: 8,
    color: COLORS.tx2,
    marginBottom: 3,
    lineHeight: 1.4,
  },
  approvalLabel: { fontFamily: "Helvetica-Bold", color: COLORS.tx },

  /* Inspection — mirrors .inspMetric */
  inspSummaryRow: { flexDirection: "row", gap: 10, marginBottom: 10 },
  inspMetric: {
    flex: 1,
    alignItems: "center",
    backgroundColor: COLORS.bg3,
    borderRadius: 8,
    paddingVertical: 10,
  },
  inspMetricVal: { fontFamily: "Helvetica-Bold", fontSize: 15 },
  inspMetricValBlue: { color: COLORS.b },
  inspMetricValAmber: { color: COLORS.amber },
  inspMetricValGreen: { color: COLORS.green },
  inspMetricLabel: {
    fontSize: 6.5,
    color: COLORS.tx3,
    letterSpacing: 0.4,
    marginTop: 3,
  },
  narrativeText: {
    fontSize: 8,
    color: COLORS.tx,
    marginBottom: 5,
    lineHeight: 1.4,
  },
  narrativeFlag: { color: COLORS.red },
  photoGrid: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 8 },
  photoThumb: {
    width: 110,
    height: 82,
    borderRadius: 6,
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    objectFit: "cover",
  },

  /* Timeline — mirrors .tlDot */
  tlRow: { flexDirection: "row", marginBottom: 10, paddingLeft: 2 },
  tlDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 2,
    marginRight: 9,
  },
  tlDotDone: { backgroundColor: COLORS.green },
  tlDotActive: { backgroundColor: COLORS.amber },
  tlDotBlue: { backgroundColor: COLORS.b },
  tlTitle: { fontSize: 8.5, fontFamily: "Helvetica-Bold", color: COLORS.tx },
  tlMeta: { fontSize: 7.5, color: COLORS.tx3, marginTop: 1, lineHeight: 1.4 },

  footer: {
    position: "absolute",
    bottom: 16,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: COLORS.bd,
    paddingTop: 5,
  },
  footerText: { fontSize: 7, color: COLORS.tx3 },
});

/* ── Helpers ─────────────────────────────────────────────────────── */

const amountOf = (m?: { amount: number } | null) => Number(m?.amount ?? 0);

const pctColor = (pct?: number | null) => {
  const n = Number(pct ?? 0);
  if (n === 100) return S.tdGreen;
  if (n === 0) return S.tdMuted;
  if (n < 30) return S.tdWarn;
  return S.td;
};

const SectionHeading = ({ title }: { title: string }) => (
  <View style={S.cardTitleRow}>
    <Text style={S.cardTitle}>{title}</Text>
  </View>
);

// Mirrors DrawHeader.tsx's STATUS_META (label + badge tone) so the PDF
// banner reads the same as the on-screen draw header.
const STATUS_META: Record<string, { label: string; badge: Style }> = {
  approved: { label: " Approved & Funded", badge: S.dhBadgeApproved },
  cancelled: { label: " Cancelled", badge: S.dhBadgeRejected },
  submitted: { label: " Pending Bank Approval", badge: S.dhBadgePending },
  lender_action: {
    label: " Pending Lender Approval",
    badge: S.dhBadgePending,
  },
  inspection_pending: {
    label: " Inspection Pending",
    badge: S.dhBadgePending,
  },
  owner_signature_pending: {
    label: " Awaiting Owner Signature",
    badge: S.dhBadgePending,
  },
  draft: { label: "Draft", badge: S.dhBadgePending },
};

// Mirrors TimelineTab.tsx's dotVariant.
const dotVariant = (activityType: string) => {
  if (activityType.includes("INSPECTION")) return S.tlDotBlue;
  if (activityType.includes("REJECT")) return S.tlDotActive;
  return S.tlDotDone;
};

// Mirrors DrawHistory.types.tsx's formatGcProgressStatus, using react-pdf
// Style objects in place of CSS classes.
const progressStatus = (status?: string): { style: Style; label: string } => {
  switch (status) {
    case "not_started":
      return { style: S.progChipNotStarted, label: "Not Started" };
    case "completed":
      return { style: S.progChipNearingComplete, label: "Completed" };
    case "nearing_complete":
      return { style: S.progChipNearingComplete, label: "Nearing Comp." };
    case "in_progress":
      return { style: S.progChipInProgress, label: "In Progress" };
    default:
      return {
        style: S.progChipInProgress,
        label: status
          ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
          : "—",
      };
  }
};

const toneColor = (tone?: "blue" | "green" | "amber" | "red") => {
  switch (tone) {
    case "blue":
      return COLORS.b;
    case "green":
      return COLORS.green;
    case "amber":
      return COLORS.amber;
    case "red":
      return COLORS.red;
    default:
      return COLORS.tx;
  }
};

/* ── Draw section (one draw's full detail — repeated per entry) ───── */

const DrawSection = ({
  entry,
  isFirst,
}: {
  entry: DrawHistoryEntry;
  isFirst: boolean;
}) => {
  const {
    drawDetail,
    timelineEvents = [],
    inspectionData,
    progressLineItems = [],
  } = entry;

  const {
    draw,
    summary: g702,
    lineItems,
    lienWaivers,
    signatures,
    approval,
  } = drawDetail;
  const drawLabel = String(draw.drawNumber).padStart(2, "0");
  const retainagePct = g702?.retainageRate
    ? parseFloat(g702.retainageRate)
    : null;

  const statusMeta = STATUS_META[draw.status ?? ""] ?? {
    label: draw.status || "—",
    badge: S.dhBadgePending,
  };
  const certifiedAmount = draw.approvedAmount ?? draw.requestedAmount;
  const contractSumToDate = g702?.contractSumToDate?.amount;
  const pctOfContract =
    contractSumToDate && certifiedAmount?.amount != null
      ? `${((certifiedAmount.amount / contractSumToDate) * 100).toFixed(1)}% of contract to date`
      : null;
  const gcName = signatures?.gc?.name;
  const bankName =
    approval?.approvedBy?.bankName || approval?.approvedBy?.branchName;
  const dhSubParts = [
    bankName,
    draw.toDate ? `Period ending ${formatDate(draw.toDate, false)}` : null,
  ].filter(Boolean);

  // Mirrors SummaryTab.tsx's row set exactly (including its 4a/4b/4 repeat
  // of totalCompletedAndStored), so the PDF's G702 card matches the tab.
  const g702Rows: {
    num: string;
    label: string;
    val: string;
    tone?: "blue" | "green" | "amber" | "red";
    isTotal?: boolean;
  }[] = [
    {
      num: "1",
      label: "Original contract sum",
      val: fmtMoney(g702?.originalContractSum),
    },
    {
      num: "1a",
      label: "SOV update / final contract",
      val: fmtMoney(g702?.sovUpdate),
    },
    {
      num: "2",
      label: "Net change by change orders",
      val: fmtMoney(g702?.netChangeByChangeOrders),
    },
    {
      num: "3",
      label: "Contract sum to date (1+1a+2)",
      val: fmtMoney(g702?.contractSumToDate),
      tone: "blue",
    },
    {
      num: "4a",
      label: "Total work completed to date",
      val: fmtMoney(g702?.totalCompletedAndStored),
    },
    {
      num: "4b",
      label: "Materials presently stored (Col F)",
      val: fmtMoney(g702?.totalCompletedAndStored),
      tone: "amber",
    },
    {
      num: "4",
      label: "Total completed & stored to date",
      val: fmtMoney(g702?.totalCompletedAndStored),
    },
    {
      num: "5",
      label: `Retainage${retainagePct != null ? ` — ${retainagePct}% of completed work` : ""}`,
      val: fmtMoney(g702?.totalRetainage),
      tone: "red",
    },
    {
      num: "6",
      label: "Total earned less retainage (4−5)",
      val: fmtMoney(g702?.totalEarnedLessRetainage),
    },
    {
      num: "7",
      label: "Less previous certificates for payment",
      val: fmtMoney(g702?.lessPreviousCertificates),
    },
    {
      num: "8",
      label: "Current payment due (6−7)",
      val: fmtMoney(g702?.currentPaymentDue),
      tone: "green",
      isTotal: true,
    },
    {
      num: "9",
      label: "Balance to finish including retainage",
      val: fmtMoney(g702?.balanceToFinish),
      tone: "amber",
    },
  ];

  const g703Rows = lineItems?.rows ?? [];
  const g703Totals = g703Rows.reduce(
    (acc, row) => ({
      sched: acc.sched + amountOf(row.sovValue),
      prev: acc.prev + amountOf(row.previousBilled),
      curr: acc.curr + amountOf(row.currentApplication),
      stored: acc.stored + amountOf(row.currentStoredMaterial),
      total: acc.total + amountOf(row.totalCompletedAndStored),
      remain: acc.remain + amountOf(row.balanceToFinish),
      retainage: acc.retainage + amountOf(row.retainageAmount),
    }),
    {
      sched: 0,
      prev: 0,
      curr: 0,
      stored: 0,
      total: 0,
      remain: 0,
      retainage: 0,
    },
  );
  const currency = g703Rows[0]?.sovValue?.currency;
  const wrapMoney = (amount: number) => ({ amount, currency });
  const g703TotalPct =
    g703Totals.sched > 0 ? (g703Totals.total / g703Totals.sched) * 100 : 0;

  const waiverItems = lienWaivers?.items ?? [];
  const invoiceItems = waiverItems.filter((item) => item?.invoice);

  const owner = signatures?.owner;
  const gc = signatures?.gc;
  const ownerSigned = !!owner?.signatureUrl;
  const gcSigned = !!gc?.signatureUrl;

  const sortedEvents = [...timelineEvents].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const isApproved = approval?.isApproved === true;

  return (
    <>
      {/* ── Draw Header (mirrors on-screen DrawHeader) ── */}
      <View style={S.dhCard} wrap={false} break={!isFirst}>
        <View>
          <Text style={[S.dhBadge, statusMeta.badge]}>{statusMeta.label}</Text>
          <Text style={S.dhTitle}>Draw #{drawLabel}</Text>
          <Text style={S.dhSub}>
            {dhSubParts.join(" · ")}
            {gcName
              ? `${dhSubParts.length ? "\n" : ""}General Contractor: ${gcName}`
              : ""}
          </Text>
        </View>
        <View style={S.dhRight}>
          <Text style={S.dhAmountLabel}>PAYMENT CERTIFIED</Text>
          <Text style={S.dhAmount}>{fmtMoney(certifiedAmount)}</Text>
          {pctOfContract ? (
            <Text style={S.dhAmountSub}>{pctOfContract}</Text>
          ) : null}
        </View>
      </View>

      {/* ── G702 Summary ── */}
        <View style={S.card}>
          <SectionHeading title="G702 — APPLICATION &amp; CERTIFICATE FOR PAYMENT" />
          {g702Rows.map((line) => (
            <View
              key={line.num}
              style={line.isTotal ? S.g702LineTotal : S.g702Line}
              wrap={false}
            >
              <View style={S.g702Left}>
                <Text style={S.g702Num}>{line.num}</Text>
                <Text style={line.isTotal ? S.g702LabelBold : S.g702Label}>
                  {line.label}
                </Text>
              </View>
              <Text
                style={[
                  line.isTotal ? S.g702ValTotal : S.g702Val,
                  { color: toneColor(line.tone) },
                ]}
              >
                {line.val}
              </Text>
            </View>
          ))}
        </View>

        {/* ── G703 Continuation Sheet ── */}
        <View style={S.card} break>
          <SectionHeading title="G703 — CONTINUATION SHEET (SCHEDULE OF VALUES)" />
          <View style={S.tHead}>
            <Text style={[S.th, S.cItem]}>Item</Text>
            <Text style={[S.th, S.cDesc]}>Description</Text>
            <Text style={[S.th, S.cMoney]}>Scheduled</Text>
            <Text style={[S.th, S.cMoney]}>Prev. App.</Text>
            <Text style={[S.th, S.cMoney]}>This Period</Text>
            <Text style={[S.th, S.cMoney]}>Materials</Text>
            <Text style={[S.th, S.cMoney]}>Total</Text>
            <Text style={[S.th, S.cPct]}>%</Text>
            <Text style={[S.th, S.cMoney]}>Balance</Text>
            <Text style={[S.th, S.cMoney]}>Retainage</Text>
          </View>
          {g703Rows.length === 0 ? (
            <Text style={S.emptyNote}>No line items billed on this draw.</Text>
          ) : (
            g703Rows.map((row, i) => (
              <View
                key={row.aiaItemId ?? row.aiaItemCode ?? i}
                style={i % 2 === 0 ? S.tRow : S.tRowAlt}
                wrap={false}
              >
                <Text style={[S.tdMuted, S.cItem]}>
                  {row.aiaItem?.itemLabel || String(row.aiaItemCode ?? i + 1)}
                </Text>
                <Text style={[S.td, S.cDesc]}>
                  {row.description || row.aiaItem?.itemName || "—"}
                </Text>
                <Text style={[S.td, S.cMoney]}>{fmtMoney(row.sovValue)}</Text>
                <Text style={[S.td, S.cMoney]}>
                  {fmtMoney(row.previousBilled)}
                </Text>
                <Text style={[S.td, S.cMoney]}>
                  {fmtMoney(row.currentApplication)}
                </Text>
                <Text style={[S.td, S.cMoney]}>
                  {fmtMoney(row.currentStoredMaterial)}
                </Text>
                <Text style={[S.td, S.cMoney]}>
                  {fmtMoney(row.totalCompletedAndStored)}
                </Text>
                <Text style={[pctColor(row.percentageComplete), S.cPct]}>
                  {row.percentageComplete ?? 0}%
                </Text>
                <Text style={[S.td, S.cMoney]}>
                  {fmtMoney(row.balanceToFinish)}
                </Text>
                <Text style={[S.td, S.cMoney]}>
                  {fmtMoney(row.retainageAmount)}
                </Text>
              </View>
            ))
          )}
          {g703Rows.length > 0 && (
            <View style={S.tTotal} wrap={false}>
              <Text style={[S.tdBold, S.cItem]}>TOTAL</Text>
              <Text style={[S.tdBold, S.cDesc]} />
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(g703Totals.sched))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(g703Totals.prev))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(g703Totals.curr))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(g703Totals.stored))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(g703Totals.total))}
              </Text>
              <Text style={[pctColor(g703TotalPct), S.tdBold, S.cPct]}>
                {g703TotalPct.toFixed(1)}%
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(g703Totals.remain))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(g703Totals.retainage))}
              </Text>
            </View>
          )}
        </View>

        {/* ── Invoices ── */}
        <View style={S.card} break>
          <SectionHeading title={`INVOICES (${invoiceItems.length})`} />
          {invoiceItems.length === 0 ? (
            <Text style={S.emptyNote}>No invoices attached to this draw.</Text>
          ) : (
            invoiceItems.map((item, i) => (
              <View key={item.id ?? i} style={S.listRow} wrap={false}>
                <Text style={[S.docIcon, S.docIconPdf]}>PDF</Text>
                <View style={S.listBody}>
                  <Text style={S.listName}>
                    {item.invoice?.fileName ?? item.description ?? "Invoice"}
                  </Text>
                  <Text style={S.listMeta}>
                    {[
                      item.subContractor?.companyName ?? item.companyName,
                      fmtMoney(item.totalAmount),
                      item.invoice?.uploadedAt
                        ? `Uploaded ${formatDate(item.invoice.uploadedAt, false)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </View>
              </View>
            ))
          )}
        </View>

        {/* ── Lien Waivers ── */}
        <View style={S.card}>
          <SectionHeading
            title={`LIEN WAIVERS (${lienWaivers?.signed ?? 0} of ${lienWaivers?.total ?? waiverItems.length} signed)`}
          />
          {waiverItems.length === 0 ? (
            <Text style={S.emptyNote}>
              No lien waivers found for this draw.
            </Text>
          ) : (
            waiverItems.map((item, i) => {
              const status = item.signedAt
                ? "signed"
                : item.invitedAt
                  ? "pending"
                  : "missing";
              const statusLabel =
                status === "signed"
                  ? " Signed"
                  : status === "pending"
                    ? "Pending"
                    : "Not Sent";
              const iconStyle =
                status === "signed"
                  ? S.docIconSigned
                  : status === "pending"
                    ? S.docIconPending
                    : S.docIconMissing;
              const chipStyle =
                status === "signed"
                  ? S.statusChipSigned
                  : status === "pending"
                    ? S.statusChipPending
                    : S.statusChipMissing;
              return (
                <View key={item.id ?? i} style={S.listRow} wrap={false}>
                  <Text style={[S.docIcon, iconStyle]}>
                    {status === "signed" ? <span>&#10003;</span> : "!"}
                  </Text>
                  <View style={S.listBody}>
                    <Text style={S.listName}>
                      {item.subContractor?.companyName ??
                        item.companyName ??
                        "—"}
                    </Text>
                    <Text style={S.listMeta}>
                      {[
                        fmtMoney(item.totalAmount),
                        item.signedAt
                          ? `Signed ${formatDate(item.signedAt, false)}`
                          : item.invitedAt
                            ? `Sent ${formatDate(item.invitedAt, false)}`
                            : "Not yet sent",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </View>
                  <Text style={[S.statusChip, chipStyle]}>{statusLabel}</Text>
                </View>
              );
            })
          )}
        </View>

        {/* ── GC Progress ── */}
        <View style={S.card} break>
          <SectionHeading title="GC SELF-REPORTED PROGRESS" />
          {progressLineItems.length === 0 ? (
            <Text style={S.emptyNote}>
              No self-reported progress available for this draw.
            </Text>
          ) : (
            progressLineItems.map((item, i) => {
              const status = progressStatus(item.status);
              return (
                <View
                  key={item.aiaItemId ?? i}
                  style={S.progChip}
                  wrap={false}
                >
                  <Text style={S.progChipNum}>{item.itemCode}</Text>
                  <Text style={S.progChipDesc}>{item.description}</Text>
                  <Text style={[S.progChipStatus, status.style]}>
                    {status.label}
                  </Text>
                </View>
              );
            })
          )}
        </View>

        {/* ── Signatures ── */}
        <View style={S.card}>
          <SectionHeading title="SIGNATURES" />
          <View style={S.sigRow}>
            <View style={S.sigCard} wrap={false}>
              <View style={S.sigCardHeader}>
                <Text>Owner / Borrower</Text>
                <Text
                  style={[
                    S.statusChip,
                    ownerSigned ? S.statusChipSigned : S.statusChipMissing,
                  ]}
                >
                  {ownerSigned ? " Signed" : "Not Signed"}
                </Text>
              </View>
              <View style={S.sigBody}>
                <View style={S.sigLine}>
                  <Text style={S.sigLabel}>Name</Text>
                  <Text style={S.sigValue}>{owner?.name ?? "—"}</Text>
                </View>
                <View style={S.sigLine}>
                  <Text style={S.sigLabel}>Title</Text>
                  <Text style={S.sigValue}>{owner?.designation ?? "—"}</Text>
                </View>
                <View style={S.sigLine}>
                  <Text style={S.sigLabel}>Date</Text>
                  <Text style={S.sigValue}>
                    {owner?.signedAt ? formatDate(owner.signedAt, false) : "—"}
                  </Text>
                </View>
              </View>
            </View>
            <View style={S.sigCard} wrap={false}>
              <View style={S.sigCardHeader}>
                <Text>General Contractor</Text>
                <Text
                  style={[
                    S.statusChip,
                    gcSigned ? S.statusChipSigned : S.statusChipMissing,
                  ]}
                >
                  {gcSigned ? " Signed" : "Not Signed"}
                </Text>
              </View>
              <View style={S.sigBody}>
                <View style={S.sigLine}>
                  <Text style={S.sigLabel}>Name</Text>
                  <Text style={S.sigValue}>{gc?.name ?? "—"}</Text>
                </View>
                <View style={S.sigLine}>
                  <Text style={S.sigLabel}>Title</Text>
                  <Text style={S.sigValue}>{gc?.designation ?? "—"}</Text>
                </View>
                <View style={S.sigLine}>
                  <Text style={S.sigLabel}>Date</Text>
                  <Text style={S.sigValue}>
                    {gc?.signedAt ? formatDate(gc.signedAt, false) : "—"}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* ── Inspection ── */}
        <View style={S.card} break>
          <SectionHeading title="CSC FIELD INSPECTION" />
          {!inspectionData?.inspection ? (
            <Text style={S.emptyNote}>
              No CSC inspection report available for this draw.
            </Text>
          ) : (
            <>
              <View style={S.inspSummaryRow}>
                <View style={S.inspMetric}>
                  <Text style={[S.inspMetricVal, S.inspMetricValBlue]}>
                    {inspectionData.inspection.cscEstimation ?? "—"}%
                  </Text>
                  <Text style={S.inspMetricLabel}>CSC ESTIMATE</Text>
                </View>
                <View style={S.inspMetric}>
                  <Text style={[S.inspMetricVal, S.inspMetricValAmber]}>
                    {inspectionData.flagCount ?? 0}
                  </Text>
                  <Text style={S.inspMetricLabel}>FLAGS RAISED</Text>
                </View>
                <View style={S.inspMetric}>
                  <Text style={[S.inspMetricVal, S.inspMetricValGreen]}>
                    {inspectionData.photos?.length ?? 0}
                  </Text>
                  <Text style={S.inspMetricLabel}>PHOTOS CAPTURED</Text>
                </View>
              </View>
              {inspectionData.inspection.note?.general && (
                <Text style={S.narrativeText}>
                  {inspectionData.inspection.note.general}
                </Text>
              )}
              {inspectionData.inspection.note?.flags && (
                <Text style={[S.narrativeText, S.narrativeFlag]}>
                  Flags: {inspectionData.inspection.note.flags}
                </Text>
              )}
              {inspectionData.inspection.note?.offsite && (
                <Text style={S.narrativeText}>
                  Off-site materials: {inspectionData.inspection.note.offsite}
                </Text>
              )}
              {(inspectionData.photos?.length ?? 0) > 0 && (
                <View style={S.photoGrid}>
                  {inspectionData.photos.map((photo) =>
                    photo.url ? (
                      <Image
                        key={photo.id}
                        style={S.photoThumb}
                        src={photo.url}
                      />
                    ) : null,
                  )}
                </View>
              )}
            </>
          )}
        </View>

        {/* ── Approval ── */}
        <View
          style={[
            S.approvalBlock,
            isApproved ? S.approvalBlockApproved : S.approvalBlockPending,
          ]}
          wrap={false}
        >
          <View
            style={[
              S.approvalIcon,
              isApproved ? S.approvalIconApproved : S.approvalIconPending,
            ]}
          >
            <Text>{isApproved ? <span>&#10003;</span> : "⏳"}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text
              style={[
                S.approvalTitle,
                isApproved ? S.approvalTitleApproved : S.approvalTitlePending,
              ]}
            >
              Draw #{drawLabel} —{" "}
              {isApproved ? "Approved & Funded" : "Awaiting Bank Approval"}
            </Text>
            {isApproved && approval?.approvedBy && (
              <Text style={S.approvalLine}>
                <Text style={S.approvalLabel}>Approved by: </Text>
                {approval.approvedBy.display ?? approval.approvedBy.name}
              </Text>
            )}
            {isApproved && approval?.approvalDate && (
              <Text style={S.approvalLine}>
                <Text style={S.approvalLabel}>Approval date: </Text>
                {formatDate(approval.approvalDate, false)}
              </Text>
            )}
            <Text style={S.approvalLine}>
              <Text style={S.approvalLabel}>Amount certified: </Text>
              {fmtMoney(
                approval?.amountCertified ??
                  draw.approvedAmount ??
                  draw.requestedAmount,
              )}
            </Text>
            {approval?.retainage && (
              <Text style={S.approvalLine}>
                <Text style={S.approvalLabel}>Retainage held this draw: </Text>
                {fmtMoney(approval.retainage.thisDraw)} (cumulative:{" "}
                {fmtMoney(approval.retainage.cumulative)})
              </Text>
            )}
            {approval?.cscRecommendation && (
              <Text style={S.approvalLine}>
                <Text style={S.approvalLabel}>CSC recommendation: </Text>
                {approval.cscRecommendation.text}
              </Text>
            )}
            {approval?.bankNotes && (
              <Text style={S.approvalLine}>
                <Text style={S.approvalLabel}>Bank notes: </Text>"
                {approval.bankNotes}"
              </Text>
            )}
          </View>
        </View>

        {/* ── Timeline ── */}
        <View style={S.card} break>
          <SectionHeading title="DRAW TIMELINE" />
          {sortedEvents.length === 0 ? (
            <Text style={S.emptyNote}>
              No timeline events recorded for this draw.
            </Text>
          ) : (
            sortedEvents.map((event) => (
              <View key={event.id} style={S.tlRow} wrap={false}>
                <View style={[S.tlDot, dotVariant(event.activityType)]} />
                <View style={{ flex: 1 }}>
                  <Text style={S.tlTitle}>{event.title}</Text>
                  <Text style={S.tlMeta}>
                    {[
                      formatDate(event.createdAt, false),
                      event.actor?.name,
                      event.description,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </View>
              </View>
            ))
          )}
        </View>
    </>
  );
};

/* ── Component ───────────────────────────────────────────────────── */

export default function DrawHistoryPdf({
  entries,
  projectName,
}: DrawHistoryPdfProps) {
  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const isSingle = entries.length === 1;
  const singleLabel = isSingle
    ? String(entries[0].drawDetail.draw.drawNumber).padStart(2, "0")
    : null;

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={S.page} wrap>
        {/* ── Header ── */}
        <View style={S.headerRow}>
          <View>
            <Text style={S.headerTitle}>
              {isSingle
                ? `Draw #${singleLabel} — Full Draw History Detail`
                : `Full Draw History Detail — All Draws (${entries.length})`}
            </Text>
            <Text style={S.headerSub}>
              Prepared by CSC New England · Read-only
            </Text>
          </View>
          <View style={S.headerRight}>
            {projectName ? (
              <Text
                style={[
                  S.headerMetaLine,
                  { fontFamily: "Helvetica-Bold", color: COLORS.tx },
                ]}
              >
                {projectName}
              </Text>
            ) : null}
            <Text style={S.headerMetaLine}>Generated: {today}</Text>
          </View>
        </View>

        {entries.map((entry, i) => (
          <DrawSection
            key={entry.drawDetail.draw.id}
            entry={entry}
            isFirst={i === 0}
          />
        ))}

        {/* Footer */}
        <View style={S.footer} fixed>
          <Text style={S.footerText}>
            CSC New England ·{" "}
            {isSingle
              ? `Draw #${singleLabel} Full Detail`
              : "Full Draw History Detail"}{" "}
            · {today}
          </Text>
          <Text
            style={S.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
